import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight, ChevronDown, Bot, Zap, Cpu, Building2, Leaf } from 'lucide-react'
import { PageMeta } from '@/components/PageMeta'
import { Section } from '@/components/ui/Section'
import { CTASection } from '@/components/sections/CTASection'

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
}

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 },
  },
  viewport: { once: true, margin: '-100px' },
}

interface SectorNavItem {
  id: string
  labelKey: string
  icon: React.ComponentType<{ className?: string }>
}

export function SectorsPage() {
  const { t } = useTranslation('pages')
  const { hash } = useLocation()

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '')
      // Small delay to let the page render
      const timer = setTimeout(() => {
        const element = document.getElementById(id)
        if (element) {
          const offset = 144
          const elementPosition = element.getBoundingClientRect().top + window.scrollY
          window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' })
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [hash])

  const sectors: SectorNavItem[] = [
    { id: 'automazione', labelKey: 'industrialAutomation.title', icon: Zap },
    { id: 'intelligenza-artificiale', labelKey: 'aiVision.title', icon: Bot },
    { id: 'distribuzione', labelKey: 'industrialDistribution.title', icon: Cpu },
    { id: 'terziario', labelKey: 'tertiarySector.title', icon: Building2 },
    { id: 'rinnovabili', labelKey: 'renewableEnergy.title', icon: Leaf },
  ]

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 144 // 80px header + 64px sticky nav
      const elementPosition = element.getBoundingClientRect().top + window.scrollY
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' })
    }
  }

  return (
    <>
      <PageMeta
        title={`${t('sectors.title')} — FG Automazioni`}
        description={t('sectors.subtitle')}
      />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 lg:px-8 py-32 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-cyan-400 text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              {t('sectors.whatWeDo')}
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
              {t('sectors.title')}
            </h1>

            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
              {t('sectors.subtitle')}
            </p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <button
                onClick={() => scrollToSection('automazione')}
                className="group inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg transition-all"
              >
                {t('sectors.explore')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-white/40"
            >
              <ChevronDown className="w-8 h-8" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <nav className="flex flex-wrap items-center justify-center gap-1 py-3">
            {sectors.map((sector) => (
              <button
                key={sector.id}
                onClick={() => scrollToSection(sector.id)}
                className="group flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-cyan-600 rounded-lg hover:bg-slate-50 transition-all"
              >
                <sector.icon className="w-4 h-4" />
                <span>{t(sector.labelKey)}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Section 1: Automazione Industriale */}
      <Section background="white" className="scroll-mt-36" id="automazione">
        <div className="mx-auto max-w-7xl">
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-cyan-600" />
              </div>
              <span className="text-sm font-semibold text-cyan-600 uppercase tracking-wider">
                {t('industrialAutomation.tag')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('industrialAutomation.title')}
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-3xl">
              {t('industrialAutomation.subtitle')}
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: '-100px' }}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {[
              { key: 'vetro', image: '/media/images/vetro-cavo-impianto-fg-automazione.webp' },
              { key: 'rifiuti', image: '/media/images/trattamento-rifiuti-impianto-fg-automazione.webp' },
              { key: 'aspirazione', image: '/media/images/aspirazione-impianto-fg-automazione.webp' },
              { key: 'legno', image: '/media/images/legno-impianto-fg-automazione.webp' },
              { key: 'nastri', image: '/media/images/trasporto-nastri-impianto-fg-automazione.webp' },
              { key: 'custom', image: '/media/images/custom-impianto-fg-automazione.webp' },
            ].map((item, index) => (
              <motion.div
                key={item.key}
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={item.image}
                    alt={t(`industrialAutomation.${item.key}Title`)}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900">
                    {t(`industrialAutomation.${item.key}Title`)}
                  </h3>
                  <p className="mt-2 text-slate-600">
                    {t(`industrialAutomation.${item.key}Desc`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* Section 2: Intelligenza Artificiale */}
      <Section background="slate" className="scroll-mt-36" id="intelligenza-artificiale">
        <div className="mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Bot className="w-6 h-6 text-purple-600" />
                </div>
                <span className="text-sm font-semibold text-purple-600 uppercase tracking-wider">
                  {t('aiVision.tag')}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                {t('aiVision.title')}
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                {t('aiVision.subtitle')}
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true, margin: '-100px' }}
              className="space-y-6"
            >
              {[
                { key: 'brainyware', image: '/media/images/brainyware.png', color: 'purple' },
                { key: 'visiofy', image: '/media/images/visione.jpg', color: 'blue' },
              ].map((item) => (
                <motion.div
                  key={item.key}
                  variants={fadeInUp}
                  transition={{ duration: 0.5 }}
                  className="group flex flex-col sm:flex-row gap-6 overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-all"
                >
                  <div className="sm:w-48 aspect-video sm:aspect-square overflow-hidden">
                    <img
                      src={item.image}
                      alt={t(`aiVision.${item.key}Title`)}
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 p-6 sm:pl-0 sm:py-6 sm:pr-6">
                    <h3 className="text-xl font-semibold text-slate-900">
                      {t(`aiVision.${item.key}Title`)}
                    </h3>
                    <p className="mt-2 text-slate-600">
                      {t(`aiVision.${item.key}Desc`)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Section 3: Distribuzione Industriale */}
      <Section background="white" className="scroll-mt-36" id="distribuzione">
        <div className="mx-auto max-w-7xl">
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-3 mb-4 px-4 py-2 bg-amber-50 rounded-full">
              <Cpu className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-semibold text-amber-600 uppercase tracking-wider">
                {t('industrialDistribution.tag')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('industrialDistribution.title')}
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              {t('industrialDistribution.subtitle')}
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: '-100px' }}
            className="grid gap-6 md:grid-cols-3"
          >
            {[
              { key: 'cabine', image: '/media/images/DISTRIBUZ.-INDUSTRIALE-CABINE-MEDIA-TENSIONE.jpg' },
              { key: 'power', image: '/media/images/distribuzione-potenza-impianto-fg-automazione.webp' },
              { key: 'illuminazione', image: '/media/images/illuminazione-industriale-impianto-fg-automazione.webp' },
            ].map((item, index) => (
              <motion.div
                key={item.key}
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group overflow-hidden rounded-2xl bg-gradient-to-b from-slate-50 to-white border border-slate-200 shadow-sm hover:shadow-lg transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt={t(`industrialDistribution.${item.key}Title`)}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900">
                    {t(`industrialDistribution.${item.key}Title`)}
                  </h3>
                  <p className="mt-2 text-slate-600">
                    {t(`industrialDistribution.${item.key}Desc`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* Section 4: Settore Terziario */}
      <Section background="slate" className="scroll-mt-36" id="terziario">
        <div className="mx-auto max-w-7xl">
          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
              <span className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">
                {t('tertiarySector.tag')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {t('tertiarySector.title')}
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-3xl">
              {t('tertiarySector.subtitle')}
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: '-100px' }}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-4"
          >
            {[
              { key: 'reti', image: '/media/images/SETTORE-TERZIARIO-RETI-CABLATE.jpg' },
              { key: 'antincendio', image: '/media/images/antincendio-impianto-fg-automazione.webp' },
              { key: 'antintrusione', image: '/media/images/antintrusione-impianto-fg-automazione.webp' },
              { key: 'domotica', image: '/media/images/Domotica.jpg' },
            ].map((item, index) => (
              <motion.div
                key={item.key}
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt={t(`tertiarySector.${item.key}Title`)}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {t(`tertiarySector.${item.key}Title`)}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600">
                    {t(`tertiarySector.${item.key}Desc`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* Section 5: Energie Rinnovabili */}
      <Section background="white" className="scroll-mt-36" id="rinnovabili">
        <div className="mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.5 }}
              className="order-2 lg:order-1"
            >
              <div className="grid gap-4">
                <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="/media/images/Imp-Fotovoltaico-1.jpg"
                    alt={t('renewableEnergy.photovoltaicAlt1')}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="/media/images/Imp-Fotovoltaico-2.jpg"
                    alt={t('renewableEnergy.photovoltaicAlt2')}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="order-1 lg:order-2"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-green-600" />
                </div>
                <span className="text-sm font-semibold text-green-600 uppercase tracking-wider">
                  {t('renewableEnergy.tag')}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                {t('renewableEnergy.title')}
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                {t('renewableEnergy.subtitle')}
              </p>
              <div className="mt-6 space-y-4 text-slate-600">
                <p>{t('renewableEnergy.p1')}</p>
                <p>{t('renewableEnergy.p2')}</p>
              </div>

              {/* Stats */}
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-bold text-green-700">-30%</div>
                  <div className="text-sm text-green-600">{t('renewableEnergy.stat1')}</div>
                </div>
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-bold text-green-700">CO₂</div>
                  <div className="text-sm text-green-600">{t('renewableEnergy.stat2')}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <CTASection />
    </>
  )
}
